import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Plus, User, Menu, Crown, LayoutDashboard, LogOut, Building2, MapPin, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";

const NAV = [
  { to: "/agricultural-land", label: "Agricultural Land" },
  { to: "/plots", label: "Plots" },
  { to: "/properties", label: "Buy & Sell" },
  { to: "/guides", label: "Guides" },
  { to: "/map", label: "Map View" },
  { to: "/premium", label: "Premium" },
];

export function Header() {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const navigate = useNavigate();

  const handlePost = () => {
    if (!user) return setAuthOpen(true);
    navigate("/list-property");
  };

  return (
    <header className="sticky top-0 z-50 bg-[#0E3B2E] text-white border-b border-[#C8A24C]/25">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <img src="/logo.png" alt="Dotlands Realty" className="h-11 w-11 object-contain" />
          <div className="leading-none">
            <div className="font-heading font-extrabold text-lg tracking-wide">
              <span className="text-[#C8A24C]">Dotlands</span> <span className="text-white">Realty</span>
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className="px-3.5 py-2 rounded-lg text-sm font-medium text-white/85 hover:text-[#C8A24C] hover:bg-white/5 transition-colors">
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button onClick={handlePost} data-testid="post-property-btn"
            className="hidden sm:inline-flex bg-[#C8A24C] hover:bg-[#A8863A] text-[#0E3B2E] font-bold rounded-full px-4">
            <Plus className="w-4 h-4 mr-1" /> Post Property
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-menu-btn" className="flex items-center gap-2 border border-white/20 rounded-full pl-1 pr-3 py-1 hover:bg-white/5">
                  <div className="w-8 h-8 rounded-full bg-[#C8A24C] text-[#0E3B2E] flex items-center justify-center text-sm font-bold">
                    {(user.name || "U").charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:block text-sm font-medium max-w-[90px] truncate">{user.name || "Account"}</span>
                  {user.is_premium && <Crown className="w-4 h-4 text-[#C8A24C]" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-xs text-slate-500 truncate">{user.phone ? `+91 ${user.phone}` : user.email} · {user.plan === "developer" ? "Developer" : user.is_premium ? "Premium" : "Free"}</div>
                <DropdownMenuSeparator />
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Owner Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/agent")} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> My Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/list-property")} data-testid="menu-my-listings">
                  <Building2 className="w-4 h-4 mr-2" /> List / My Properties
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favourites")} data-testid="menu-favourites">
                  <Heart className="w-4 h-4 mr-2" /> Saved Properties
                </DropdownMenuItem>
                {!user.is_premium && (
                  <DropdownMenuItem onClick={() => navigate("/premium")} data-testid="menu-premium">
                    <Crown className="w-4 h-4 mr-2 text-[#C8A24C]" /> Upgrade to Premium
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" onClick={() => setAuthOpen(true)} data-testid="login-btn" className="rounded-full bg-transparent border-[#C8A24C]/50 text-white hover:bg-[#C8A24C] hover:text-[#0E3B2E]">
              <User className="w-4 h-4 mr-1.5" /> Login
            </Button>
          )}

          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2" data-testid="mobile-menu-btn"><Menu className="w-6 h-6" /></button>
            </SheetTrigger>
            <SheetContent side="right">
              <div className="flex flex-col gap-1 mt-8">
                {NAV.map((n) => (
                  <Link key={n.label} to={n.to} className="px-3 py-3 rounded-lg text-base font-medium hover:bg-slate-100 flex items-center gap-2">
                    {n.label === "Map View" && <MapPin className="w-4 h-4" />} {n.label}
                  </Link>
                ))}
                <Button onClick={handlePost} className="mt-4 bg-[#0E3B2E] hover:bg-[#0A2A20]">
                  <Plus className="w-4 h-4 mr-1" /> Post Property
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
}
